/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import {
  ActionsObservable,
  Epic,
  ofType,
  StateObservable,
} from "redux-observable"
import { getActionSuccess } from "store/duck/utils/actionUtils"
import { actions, actionTypes } from "store/duck/bulkUpload/actions"
import { map, switchMap, takeUntil, delay, mapTo } from "rxjs/operators"
import { AxiosAction } from "types/api"
import {
  GetBulkUploadTaskRequest,
  GetBulkUploadTaskResponse,
} from "types/delv-platform-api/bulkUploadTask"
import _omit from "lodash/omit"
import { EMPTY, of, timer } from "rxjs"

import { AppState } from "store/store.type"
import {
  BULK_UPLOAD_TABLE_RECORD_LIMIT,
  BULK_TASK_POLLING_INTERVAL,
} from "constants/menu_bulk_upload"
import { isOngoingTask } from "./utils"

const ongoingBulkTaskPollingObservable = (
  action$: ActionsObservable<any>,
  params: Pick<GetBulkUploadTaskRequest, "uploadType">,
  merchantID: string,
  merchantGroupID: string,
  menuGroupID: string,
  startImmediately?: boolean,
) =>
  timer(
    startImmediately ? 0 : BULK_TASK_POLLING_INTERVAL,
    BULK_TASK_POLLING_INTERVAL,
  ).pipe(
    takeUntil(action$.pipe(ofType(actionTypes.CANCEL_POLLING_BULK_TASKS))),
    map(() => {
      return actions.pollingBulkTasks(
        {
          uploadType: params.uploadType,
          limit: BULK_UPLOAD_TABLE_RECORD_LIMIT, // tasks are sorted by created time by default, ongoing tasks should always being on top of the list
          menuGroupID,
        },
        merchantID,
        merchantGroupID,
      )
    }),
  )

// currently only 1 bulk task is allowed at a time, able to create a new bulk task means there's no ongoing task -> no running polling -> no need to cancel polling
// but we still cancel the running polling here in case we allow multiple bulk upload later
// split into 2 epics for easy testing sake stopPollingOngoingBulkTaskAfterUploadingEpic and startNewPollingOngoingBulkTaskAfterUploadingEpic
const stopPollingOngoingBulkTaskAfterUploadingEpic: Epic = action$ =>
  action$.pipe(
    ofType(getActionSuccess(actionTypes.COMPLETE_BULK_TASK)),
    mapTo({ type: actionTypes.CANCEL_POLLING_BULK_TASKS }),
  )

const startNewPollingOngoingBulkTaskAfterUploadingEpic: Epic = (
  action$,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType(getActionSuccess(actionTypes.COMPLETE_BULK_TASK)), // TODO check with BE how about if upload success but COMPLETE_BULK_TASK failed
    delay(1000),
    switchMap(
      (
        action: AxiosAction<any, ReturnType<typeof actions.completeBulkTask>>,
      ) => {
        const {
          merchantID,
          merchantGroupID,
          menuGroupDetail: { menuGroupID },
        } = state$.value.menu || {}

        const uploadType =
          action.meta?.previousAction?.payload?.request?.data?.uploadType

        return ongoingBulkTaskPollingObservable(
          action$,
          {
            uploadType,
          },
          merchantID,
          merchantGroupID,
          menuGroupID,
          true,
        )
      },
    ),
  )

const stopPollingOngoingBulkTaskEpic: Epic = action$ =>
  action$.pipe(
    ofType(getActionSuccess(actionTypes.POLLING_BULK_TASKS)),
    // currently only have 1 polling, don't use switchMap if there's more than 1 polling
    switchMap(
      (
        action: AxiosAction<
          GetBulkUploadTaskResponse,
          ReturnType<typeof actions.pollingBulkTasks>
        >,
      ) => {
        const { tasks } = action.payload?.data || {}

        if (
          !tasks ||
          tasks.length === 0 ||
          tasks.every(task => !isOngoingTask(task))
        ) {
          return of({
            type: actionTypes.CANCEL_POLLING_BULK_TASKS,
          })
        }

        return EMPTY
      },
    ),
  )

const pollingOngoingBulkTaskEpic: Epic = (
  action$,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType(getActionSuccess(actionTypes.FETCH_BULK_TASKS)),
    switchMap(
      (
        action: AxiosAction<
          GetBulkUploadTaskResponse,
          ReturnType<typeof actions.fetchBulkTasks>
        >,
      ) => {
        const {
          merchantID,
          merchantGroupID,
          menuGroupDetail: { menuGroupID },
        } = state$.value.menu || {}

        const isOngoingTaskExist = action.payload?.data?.tasks?.some(task =>
          isOngoingTask(task),
        )

        const { params } = (action.meta?.previousAction as any)?.meta
          ?.requestParams
          ? { params: (action.meta?.previousAction as any).meta.requestParams }
          : action.meta?.previousAction?.payload?.request || {}
        // should only start polling interval for the initial fetch (the fetch without any filter)
        const isTheFetchWithoutFilter =
          params &&
          Object.values(
            _omit<GetBulkUploadTaskRequest, keyof GetBulkUploadTaskRequest>(
              params,
              ["uploadType", "limit", "menuGroupID"],
            ), // the endpoint will have some filter fields later, by using omit, no need to update this logic for those new optional fields
          ).every(value => !value)

        if (isTheFetchWithoutFilter && isOngoingTaskExist) {
          return ongoingBulkTaskPollingObservable(
            action$,
            params,
            merchantID,
            merchantGroupID,
            menuGroupID,
          )
        }

        return EMPTY
      },
    ),
  )

export const epics = {
  pollingOngoingBulkTaskEpic,
  stopPollingOngoingBulkTaskEpic,
  stopPollingOngoingBulkTaskAfterUploadingEpic,
  startNewPollingOngoingBulkTaskAfterUploadingEpic,
}
